import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Container,
  Fab,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import CloneButton from "components/CloneButton";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  BaseFilter,
  EmailTemplateModel,
  ListRequestOfBaseFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import CloneEmailTemplateDialog from "./CloneEmailTemplateDialog";

interface EmailTemplateModelWithId extends EmailTemplateModel {
  id: string;
}

const EmailTemplatesList = () => {
  const { formatMessage: t } = useIntl();
  const {
    emailTemplateCreateUrl,
    emailTemplateDetailsUrl,
    emailService,
  } = useGlobalContext();
  const navigate = useNavigate();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [
    emailTemplateIdToClone,
    setEmailTemplateIdToClone,
  ] = useState<string>();
  const searchTerm = persistanceStore.useState(
    (s) => s.emailTemplateSearchTerm
  );
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);
  const { organizationId } = contextStore.useState();

  useListTitle("emailTemplates");

  const { isError, data, isFetching } = useQuery(
    [
      "emailTemplates",
      organizationId,
      debouncedSearchTerm,
      page,
      rowsPerPage,
      order,
      orderBy,
    ],
    () =>
      emailService.getEmailTemplates(
        ListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: BaseFilter.fromJS({
            searchText: debouncedSearchTerm,
          }),
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, organizationId]);

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "subject",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "subject" }),
    },
    {
      accessor: "organizationName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "organization" }),
      customSortAccessor: "organization.name",
    },
    {
      accessor: "value",
      align: "center",
      width: 0,
      type: "node",
      View: ({ value }) => (
        <CloneButton onClick={() => setEmailTemplateIdToClone(value.id)} />
      ),
      noSort: true,
      label: t({ id: "actions" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(emailTemplateDetailsUrl(id));
  };

  const emailTemplates = (data?.data ?? []) as EmailTemplateModelWithId[];

  if (isError) {
    return (
      <Alert severity="error">
        <T id="fetchError" />
      </Alert>
    );
  }

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Fab
          aria-label="add"
          color="primary"
          onClick={() => navigate(emailTemplateCreateUrl())}
        >
          <AddIcon />
        </Fab>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          value={searchTerm ?? ""}
          placeholder={t({ id: "searchPlaceholder" })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    persistanceStore.update((s) => {
                      s.emailTemplateSearchTerm = "";
                    })
                  }
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={(e) => {
            persistanceStore.update((s) => {
              s.emailTemplateSearchTerm = e.target.value;
            });
          }}
        />
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: emailTemplates,
          loading: isFetching,
          dense: true,
          title: t({
            id: "emailTemplates",
          }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) =>
              emailTemplateDetailsUrl(id)
            ),
          ],
        }}
      />

      <CloneEmailTemplateDialog
        emailTemplateId={emailTemplateIdToClone}
        close={() => setEmailTemplateIdToClone(undefined)}
      />
    </Container>
  );
};

export default EmailTemplatesList;
